import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { InsightService } from '@sc/data-access-http-services';
import { Dossier } from '@sc/data-access-models';
import { AuthService } from '@sc/feature-auth-okta';

import { AsyncPipe } from '@angular/common';
import { TranslatorService } from '@sc/data-access-translation';
import {
  AllDashBoardColulmnsToDisplay,
  DossierSheetColumnName,
  ExcelFileExporterService,
  InsurerDashBoardColulmnsToDisplay,
} from '@sc/feature-file-export';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { combineLatest } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { FilterDossiersStates } from '../../models/dossiers-states';
import { DossiersSandboxService } from '../../services';
import { getAppointmentApplicableFilter } from '../../types';
import { DossiersSearchBarComponent } from '../dossiers-search-bar/dossiers-search-bar.component';
import { DossiersSearchResultComponent } from '../dossiers-search-result/dossiers-search-result.component';

@Component({
  selector: 'sc-dossiers-search-page',
  styleUrls: ['./dossiers-search.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DossiersSearchBarComponent,
    DossiersSearchResultComponent,
    AsyncPipe,
  ],
  template: `
    <sc-dossiers-search-bar
      [initValue]="initValue$ | async"
      (searchEvent)="search($event)"
      (exportDossiers)="onExportDossiers()"
      [loading]="isLoadingDossiers$ | async"
      [dossiersStates]="dossiersStates$ | async"
      [hasValidatedRequestApts]="hasValidatedRequestApts"
      [displayRequestedFilters]="hasRequestAppointmentBrand"
      [displayOnlyRequestedFilters]="isFitterB2B"
      [canExport]="(dossiers$ | async)?.length > 0"
    ></sc-dossiers-search-bar>
    <sc-dossiers-search-result
      [dossiers]="dossiers$ | async"
      [hasValidatedRequestApts]="hasValidatedRequestApts"
      (consult)="consultDossier($event)"
    ></sc-dossiers-search-result>
  `,
})
export class DossiersSearchComponent implements OnInit {
  isLoadingDossiers$ = this.dossiersSB.isLoadingDossiers$;
  dossiers$ = this.dossiersSB.dossiers$;
  initValue$ = this.dossiersSB.search$;
  dossiersStates$ = this.dossiersSB.dossiersStates$;
  dossiers: Dossier[] = [];

  dossierStates: FilterDossiersStates;

  isFitterB2B: boolean;
  hasValidatedRequestApts: boolean;
  hasRequestAppointmentBrand: boolean;

  constructor(
    private readonly insightService: InsightService,
    private readonly dossiersSB: DossiersSandboxService,
    private readonly authService: AuthService,
    private readonly translator: TranslatorService,
    private readonly excelFileExporterService: ExcelFileExporterService,
    private readonly featureService: FeatureFlippingManagerService,
    private readonly router: Router
  ) {
    this.insightService.trackPageView({
      name: 'Dossiers search page',
    });
  }

  ngOnInit(): void {
    this.hasRequestAppointmentBrand =
      this.featureService.brandFeatureActivatedExists(
        'b2b-allow-appointment-request'
      ) ||
      this.featureService.brandFeatureActivatedExists(
        'b2c-allow-appointment-request'
      );
    this.hasValidatedRequestApts =
      this.featureService.brandFeatureActivatedExists(
        'can-validate-request-appointment'
      );

    combineLatest([
      this.initValue$,
      this.dossiersStates$,
      this.authService.userIsFitterB2B$(),
    ])
      .pipe(take(1))
      .subscribe(([searchValue, searchDossiersState, isFitterB2B]) => {
        this.isFitterB2B = isFitterB2B;
        this.search({
          value: searchValue,
          dossiersStates: {
            ...searchDossiersState,
            inProgress: isFitterB2B ? false : searchDossiersState.inProgress,
            complete: isFitterB2B ? false : searchDossiersState.complete,
            cancelled: isFitterB2B ? false : searchDossiersState.cancelled,
            abandoned:
              isFitterB2B || !this.canAbandonDossier()
                ? false
                : searchDossiersState.abandoned,
            requested: isFitterB2B ? true : searchDossiersState.requested,
            requestedAndValidated: isFitterB2B
              ? true
              : searchDossiersState.requestedAndValidated,
          },
        });
      });
  }

  search(search) {
    this.dossierStates = search?.dossiersStates;

    combineLatest([
      this.authService.userIsFitterB2B$(),
      this.authService.getFitterB2BBrand$(),
      this.authService.userIsBrand$(),
      this.authService.getUserBrandX$(),
    ])
      .pipe(
        take(1),
        switchMap(([isFitterB2B, fitterB2BBrand, isUserBrand, userBrand]) => {
          const fitters =
            isFitterB2B || isUserBrand
              ? [
                  {
                    fitterId: null,
                    brandId: fitterB2BBrand || userBrand,
                  },
                ]
              : undefined;

          return this.dossiersSB.searchDossiers(
            search?.searchText,
            search?.dossiersStates,
            search?.creationDates,
            search?.fitters ?? fitters,
            search?.searchCriteria
          );
        })
      )
      .subscribe((result) => {
        this.dossiers = result?.payload?.dossiers;
      });
  }

  consultDossier(dossier: Dossier) {
    this.authService.setCurrentDossierId(dossier.id);

    if (
      getAppointmentApplicableFilter(dossier) === 'NEW' &&
      !dossier?.requestAppointment
    ) {
      this.router
        .navigateByUrl('declaration/customer-data')
        .catch((err) => console.error(err));
    } else {
      this.router
        .navigateByUrl('declaration/declaration-end')
        .catch((err) => console.error(err));
    }
  }

  canAbandonDossier() {
    return this.featureService.isFeatureActivated('b2b-allow-abandon-files');
  }

  canCancelAppointment() {
    return this.featureService.brandFeatureActivatedExists(
      'can-do-appointment-cancel-fitter'
    );
  }

  async onExportDossiers() {
    this.excelFileExporterService.initNewWorkbook();
    const finishedDossiers: Dossier[] = [];
    const dossiersProgrammed: Dossier[] = [];
    const dossiersAbandoned: Dossier[] = [];
    const dossiersApmtCancel: Dossier[] = [];
    const dossiersRequested: Dossier[] = [];
    const dossiersRequestedAndValidated: Dossier[] = [];

    this.dossiers.forEach((d) => {
      if (d.state === 'ABANDONED' && this.canAbandonDossier()) {
        dossiersAbandoned.push(d);
      } else if (
        d.appointment?.status === 'CANCELLED' ||
        d.requestAppointment?.status === 'CANCELLED' ||
        d.state === 'CANCELLED'
      ) {
        dossiersApmtCancel.push(d);
      } else if (
        (!!d.appointment?.brandId && !!d.appointment?.status) ||
        (!!d.requestAppointment?.brandId &&
          !!d.requestAppointment?.status &&
          !this.hasRequestAppointmentBrand)
      ) {
        finishedDossiers.push(d);
      } else if (
        !!d.requestAppointment?.brandId &&
        !!d.requestAppointment?.status &&
        this.hasRequestAppointmentBrand
      ) {
        if (d?.requestAppointment?.isValidatedByFitter === true) {
          dossiersRequestedAndValidated?.push(d);
        } else dossiersRequested?.push(d);
      } else if (
        d.state === 'COMPLETE' &&
        d.appointment &&
        !d.appointment.status &&
        d.previousAppointments.length > 0 &&
        d.previousAppointments[0].brandId &&
        d.previousAppointments[0].status
      ) {
        // Specifc case : an update of appointment was started but not finished (no status on app)
        finishedDossiers.push(d);
      } else {
        dossiersProgrammed.push(d);
      }
    });

    const colulmnToRemove: DossierSheetColumnName[] =
      AllDashBoardColulmnsToDisplay.filter(
        (v) => InsurerDashBoardColulmnsToDisplay.indexOf(v) == -1
      );

    if (this.dossierStates?.inProgress) {
      const excludeKeys: DossierSheetColumnName[] = [
        'apmtCancelComment',
        'abandonmentComment',
        'abandonDate',
        'sentToBill',
        'noShow',
        'abTestPhone',
      ];

      this.excelFileExporterService.addDossiersPage(
        this.translator.getInstantTranslation(
          'Dashboard.export.excel.dossiers.titleProgrammed'
        ),
        dossiersProgrammed,
        [],
        excludeKeys.concat(colulmnToRemove)
      );
    }

    if (this.dossierStates?.complete) {
      const excludeKeys: DossierSheetColumnName[] = [
        'apmtCancelComment',
        'abandonmentComment',
        'abandonDate',
        'sentToBill',
        'noShow',
        'lastStep',
      ];

      this.excelFileExporterService.addDossiersPage(
        this.translator.getInstantTranslation(
          'Dashboard.export.excel.dossiers.title'
        ),
        finishedDossiers,
        [],
        excludeKeys.concat(colulmnToRemove)
      );
    }
    if (this.dossierStates?.cancelled && this.canCancelAppointment()) {
      const excludeKeys: DossierSheetColumnName[] = [
        'abandonmentComment',
        'abandonDate',
        'sentToBill',
        'noShow',
        'lastStep',
      ];

      this.excelFileExporterService.addDossiersPage(
        this.translator.getInstantTranslation(
          'Dashboard.export.excel.dossiers.titleApmtCancelled'
        ),
        dossiersApmtCancel,
        [],
        excludeKeys.concat(colulmnToRemove)
      );
    }
    if (this.dossierStates?.abandoned && this.canAbandonDossier()) {
      const excludeKeys: DossierSheetColumnName[] = [
        'apmtCancelComment',
        'sentToBill',
        'noShow',
        'lastStep',
      ];

      this.excelFileExporterService.addDossiersPage(
        this.translator.getInstantTranslation(
          'Dashboard.export.excel.dossiers.titleAbandoned'
        ),
        dossiersAbandoned,
        [],
        excludeKeys.concat(colulmnToRemove)
      );
    }

    if (this.dossierStates?.requested && this.hasRequestAppointmentBrand) {
      const excludeKeys: DossierSheetColumnName[] = [
        'apmtCancelComment',
        'abandonmentComment',
        'abandonDate',
        'sentToBill',
        'noShow',
        'abTestPhone',
      ];

      this.excelFileExporterService.addDossiersPage(
        this.translator.getInstantTranslation(
          this.hasValidatedRequestApts
            ? 'Dashboard.export.excel.dossiers.titleRequested'
            : 'Dashboard.export.excel.dossiers.titleRequestedGeneral'
        ),
        dossiersRequested,
        [],
        excludeKeys.concat(colulmnToRemove)
      );
    }

    if (
      this.dossierStates?.requestedAndValidated &&
      this.hasRequestAppointmentBrand &&
      this.hasValidatedRequestApts
    ) {
      const excludeKeys: DossierSheetColumnName[] = [
        'apmtCancelComment',
        'abandonmentComment',
        'abandonDate',
        'sentToBill',
        'noShow',
        'abTestPhone',
      ];

      this.excelFileExporterService.addDossiersPage(
        this.translator.getInstantTranslation(
          'Dashboard.export.excel.dossiers.titleRequestedAndValidated'
        ),
        dossiersRequestedAndValidated,
        [],
        excludeKeys.concat(colulmnToRemove)
      );
    }

    await this.excelFileExporterService.exportFile(
      this.translator.getInstantTranslation(
        'FitterDossierSearchPage.excelExportFields.fileName'
      ) + '.xlsx'
    );
  }
}
